import type { Brand } from "@nephroflow/design-system";

type CustomStorageEvent<T> = Omit<StorageEvent, "key" | "newValue" | "oldValue"> & {
  key: string;
  newValue: T | null;
  oldValue: T | null;
};

type StorageKey<T> = string & Brand<"storage_key", T>;

interface StorageOptions {
  userId?: string;
}

interface StorageChange<T> {
  key: string;
  newValue: T | null;
}

class StorageHelper extends EventTarget {
  #storage: Storage;

  constructor(storage: Storage = localStorage) {
    super();
    this.#storage = storage;
  }

  has<T>(key: StorageKey<T>, options?: StorageOptions) {
    return this.getRaw(key, options) !== null;
  }

  get<T>(key: StorageKey<T>, options?: StorageOptions) {
    return decodeStorageValue<T>(this.getRaw(key, options));
  }

  getRaw<T>(key: StorageKey<T>, options?: StorageOptions) {
    try {
      return this.#storage.getItem(this.#createKey(key, options));
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  }

  set<T>(key: StorageKey<T>, value: T, options?: StorageOptions) {
    this.setRaw(key, encodeStorageValue(value), options);
  }

  setRaw<T>(key: StorageKey<T>, value: string, options?: StorageOptions) {
    try {
      const scopedKey = this.#createKey(key, options);

      this.#storage.setItem(scopedKey, value);
      this.#dispatchChangeEvent(scopedKey, value);
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  }

  remove<T>(key: StorageKey<T>, options?: StorageOptions) {
    try {
      const scopedKey = this.#createKey(key, options);

      this.#storage.removeItem(scopedKey);
      this.#dispatchChangeEvent(scopedKey, null);
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  }

  clear() {
    try {
      this.#storage.clear();
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  }

  listenOutsideWindow<T>(
    key: StorageKey<T>,
    callback: (event: CustomStorageEvent<T>) => void,
    options?: StorageOptions,
  ) {
    const listener = (event: StorageEvent) => {
      if (event.storageArea === this.#storage && event.key === this.#createKey(key, options) && event.isTrusted) {
        const customEvent: CustomStorageEvent<T> = {
          ...event,
          key,
          newValue: decodeStorageValue<T>(event.newValue),
          oldValue: decodeStorageValue<T>(event.oldValue),
        };

        callback(customEvent);
      }
    };

    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }

  listen<T>(key: StorageKey<T>, callback: (event: StorageChange<T>) => void, options?: StorageOptions) {
    const listener = (({ detail }: CustomEvent<StorageChange<T>>) => {
      if (detail.key === this.#createKey(key, options)) {
        callback({ key, newValue: detail.newValue });
      }
    }) as EventListener;

    this.addEventListener("storageChange", listener);

    return () => {
      this.removeEventListener("storageChange", listener);
    };
  }

  #dispatchChangeEvent<T>(key: string, newValue: string | null) {
    this.dispatchEvent(
      new CustomEvent<StorageChange<T>>("storageChange", {
        detail: { key, newValue: decodeStorageValue<T>(newValue) },
      }),
    );
  }

  #createKey<T>(key: StorageKey<T>, options?: StorageOptions) {
    if (options?.userId) return `__wb_${options.userId}_${key}__`;

    return `__wb_${key}__`;
  }
}

function encodeStorageValue(value: any) {
  return JSON.stringify(value);
}

function decodeStorageValue<T>(json: string | null) {
  if (json === null) return null;

  let decoded: T | null = null;
  try {
    decoded = JSON.parse(json);
  } catch {
    //
  }

  return decoded;
}

function throwNoLocalStorageError(error: any): never {
  const noLocalStorageError = new Error(error.message, { cause: error });
  noLocalStorageError.name = "NoLocalStorageError";

  throw noLocalStorageError;
}

/**
 * Lazily create one instance of `StorageHelper` for each type of storage.
 * It is not possible to create storage variables in the file itself as React Router creates a server index html file even in SPA mode,
 * so browser APIs are not available.
 * See: https://reactrouter.com/how-to/spa#important-note
 */

let local: StorageHelper;
let session: StorageHelper;

export const Storage = {
  get local() {
    try {
      return (local ||= new StorageHelper(localStorage));
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  },

  get session() {
    try {
      return (session ||= new StorageHelper(sessionStorage));
    } catch (error) {
      throwNoLocalStorageError(error);
    }
  },

  key<T>(key: string) {
    return key as StorageKey<T>;
  },
};
